import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

/**
 * Tela de solicitação de recuperação de senha (DEM-2044).
 *
 * Dispara o fluxo canônico do Supabase Auth (`resetPasswordForEmail`). O link
 * enviado por email aponta para `${origin}/reset-password` — em produção,
 * `origin` é https://raios.app (Site URL/Redirect URLs corrigidos via migration
 * de config do Supabase Auth). NÃO reintroduz signup: é só recuperação.
 *
 * Privacidade: a UI sempre confirma o envio (mesmo se o email não existir) para
 * não vazar quais emails têm conta — comportamento padrão do Supabase Auth.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar a recuperação de senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-raios-bg px-4">
      <div className="w-full max-w-sm md:max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/raios-mark.png"
            alt="RAIOS"
            className="inline-block w-14 h-14 rounded-2xl mb-4 object-cover"
          />
          <h1 className="text-3xl font-bold text-raios-text tracking-tight">RAIOS</h1>
          <p className="text-raios-muted text-sm mt-1">Recuperação de senha</p>
        </div>

        {/* Card */}
        <Card className="p-0">
          <CardHeader>
            <CardTitle>Esqueci minha senha</CardTitle>
          </CardHeader>

          <CardContent>
            {sent ? (
              <div className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Se existir uma conta para <span className="font-medium text-raios-text">{email}</span>,
                  enviamos um link de recuperação. Verifique sua caixa de entrada (e o spam).
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setSent(false);
                    setEmail('');
                  }}
                >
                  Enviar para outro email
                </Button>
                <div className="text-center">
                  <Link to="/login" className="text-sm text-raios-accent hover:underline">
                    Voltar para o login
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-raios-text-secondary">
                  Informe seu email e enviaremos um link para você redefinir sua senha.
                </p>
                <Input
                  label="Email"
                  type="email"
                  placeholder="voce@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  error={error || undefined}
                />

                <Button
                  type="submit"
                  variant="brand"
                  className="w-full"
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? 'Enviando...' : 'Enviar link de recuperação'}
                </Button>

                <div className="text-center">
                  <Link to="/login" className="text-sm text-raios-accent hover:underline">
                    Voltar para o login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
